package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * SPX 0DTE Iron Condor (Dealer-Gamma-Filter) — Remote-Typisierung analog 45DTE Strangle.
 *
 * Struktur, Zeitfenster und Stop sind feste Strategie-Regeln; die Remote-Params spiegeln exakt
 * das, was auch die Desktop-View anbietet: Konto, Anzahl, Transmit, manuelle Freigabe nach einer
 * Drawdown-Pause, Vorbedingungen ignorieren und optional den zeitgesteuerten Lauf (10:25/10:30 NY).
 *
 * Der Condor hat VIER Beine — Vorschau und Bestätigung arbeiten mit einer Kontrakt-Liste.
 */

val SPX_IRON_CONDOR_STRATEGY_ID = StrategyId("spxIronCondor")
const val SPX_IRON_CONDOR_SCHEMA_VERSION = 1

@Serializable
@SerialName("spxIronCondor")
data class SpxIronCondorParams(
    /** Konto (dynamische Auswahl, [SpxIronCondorOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Anzahl Condors 1..1000 (Grenze der Desktop-View) */
    val quantity: Int,
    /**
     * Manuelle Freigabe trotz Drawdown-Pause des Journals — nur wirksam, wenn
     * [SpxIronCondorOptions.paused] gesetzt ist (Pendant zur Checkbox der Desktop-View).
     */
    val manualRelease: Boolean = false,
    /** Tagesfilter übergehen — desktopseitig an FEATURE_IGNORE_PRECONDITION gebunden (teamintern). */
    val ignorePreconditions: Boolean = false,
    /**
     * Zeitgesteuerter Lauf (Signal 10:25, Kette + Order 10:30 NY) — desktopseitig an
     * FEATURE_TIME_BASED_TRADING gebunden (teamintern); platziert ohne erneute Bestätigung.
     */
    val waitForWindow: Boolean = false,
    /**
     * Vom Nutzer bestätigte Condor-Beine in Symbol-Notation, aus `strategy.preview`
     * ([SpxIronCondorPreview.contracts]). PFLICHT für den Sofort-Start: Der Desktop ermittelt
     * die Beine beim Platzieren erneut und platziert nur bei Übereinstimmung (Compliance).
     * Nicht in Defaults/Presets persistieren.
     */
    val expectedContracts: List<String>? = null,
    override val schemaVersion: Int = SPX_IRON_CONDOR_SCHEMA_VERSION,
) : StrategyParams

@Serializable
@SerialName("spxIronCondor")
data class SpxIronCondorOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /** Lizenz-Freigaben der teaminternen Schalter ([ChecklistGates]); Default gesperrt. */
    override val ignorePreconditionsAllowed: Boolean = false,
    override val waitForWindowAllowed: Boolean = false,
    /** Journal hat die Drawdown-Pause ausgelöst — erst dann wird „Manuelle Freigabe" angeboten. */
    val paused: Boolean = false,
) : StrategyOptions, ChecklistGates

/** Ergebnis von `strategy.preview`: die vier ermittelten Beine plus alle Orders, die platziert würden. */
@Serializable
data class SpxIronCondorPreview(
    /** Symbol-Notation der ermittelten Beine; leer = Ermittlung fehlgeschlagen (siehe [message]). */
    val contracts: List<String> = emptyList(),
    /** Anzeigefertige Zeile pro Order (Entry, Stop, Stückzahl). */
    val orders: List<String> = emptyList(),
    val message: String? = null,
    val messageText: RemoteText? = null,
    val asOf: Instant,
)

@Serializable
@SerialName("spxIronCondor")
data class SpxIronCondorStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val steps: List<RemoteChecklistStep> = emptyList(),
    /** Tagesfilter wurden für diesen Lauf übersprungen */
    val preconditionsIgnored: Boolean = false,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = SPX_IRON_CONDOR_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class SpxIronCondorBounds(
    val maxQuantity: Int? = 10,
    val allowTransmitToExchange: Boolean = false,
) : ParamsBounds<SpxIronCondorParams> {

    override fun violations(params: SpxIronCondorParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: SpxIronCondorParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxQuantity != null && params.quantity > maxQuantity) {
            violations += RemoteText(
                de = "quantity ${params.quantity} überschreitet das Limit $maxQuantity",
                en = "quantity ${params.quantity} exceeds the limit $maxQuantity",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

fun validateSpxIronCondor(params: SpxIronCondorParams, options: SpxIronCondorOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != SPX_IRON_CONDOR_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $SPX_IRON_CONDOR_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $SPX_IRON_CONDOR_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $SPX_IRON_CONDOR_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.quantity !in 1..1000) {
        errors += ValidationError(
            "quantity",
            "Anzahl muss zwischen 1 und 1000 liegen",
            messageText = RemoteText(
                de = "Anzahl muss zwischen 1 und 1000 liegen",
                en = "Quantity must be between 1 and 1000",
            ),
        )
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    errors += checklistGateErrors(params.ignorePreconditions, params.waitForWindow, options)
    return errors
}
